import { cache, createAsync, useIsRouting, useParams } from "@solidjs/router";
import { Accessor, onMount, Show } from "solid-js";
import ClientOnlyComponent, {
  ClientComponent,
} from "~/client_only_components/client_component";
import BrandPageComponent from "~/components/brand_l2/brand_l2_page";
import { useBrandPage } from "~/components/brand_l2/useBrandL2";
import { useModal } from "~/components/modal";
import { SdkTopRightCloseButton } from "~/components/sdk_tr_close_button";
import { WarningModal } from "~/screens/warning_modal/warning_modal";
import { PaymentLoader } from "~/screens/payment_options/payment_loader";
import UserInfoModal from "~/components/login-rnr/user_info_modal";
import {
  BrandL2RouteData,
  getBrandL2RouteData,
} from "~/server/data/brand_l2_route_data";
import { PreReviewPopupDetails } from "~/server/types/brand";
import { pushPage, HubbleRoute } from "~/shared_states/modal";
import attachPopStateListener from "~/utils/popstate_listener";
import { DottedLoader } from "~/widgets/loader";

const getBrandL2RouteData$C = cache(getBrandL2RouteData, "brand-l2");

function openWarningModal(props: {
  onContinue: () => void;
  preReviewPopupDetails: PreReviewPopupDetails;
  modalContext: ReturnType<typeof useModal>;
}) {
  const {
    setOuterFrameClass,
    updateModalContentGetter,
    setIsModalOn,
    closeModal,
  } = props.modalContext!;

  setOuterFrameClass("sm:w-[416px]");
  updateModalContentGetter(() => {
    return (
      <WarningModal
        {...props}
        onClose={() => {
          closeModal();
        }}
      />
    );
  });
  setIsModalOn(true);
}

function openPaymentLoader(props: {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}) {
  pushPage(
    () => (
      <PaymentLoader
        amount={props.amount}
        onPaymentComplete={props.onPaymentComplete}
        onPaymentFailed={props.onPaymentFailed}
        orderId={props.orderId}
        reInit={props.reInit}
        onClose={props.onClose}
        brandImageUrl={props.brandImageUrl}
      />
    ),
    {
      isModal: true,
      disableCloseButton: true,
      preventClose: true,
      enterAnimation: false,
    },
    HubbleRoute.PAYMENT_LOADER
  );
}

function createUserInfoModalHandler() {
  const modal = useModal();

  return (props: {
    offerConstruct?: any;
    offerConstructTenure: any;
    clientLogoUrl?: string;
    redemptionAmount: any;
    dueAmount: any;
    dueAmount2: any;
    brandDetails?: any;
    selectedAmount: any;
    email: string | null;
    phone: string | null;
    name: string | null;
    onProfileUpdateSuccess: () => void;
    subtitle: string;
  }) => {
    if (!modal) return;

    const { setOuterFrameClass, updateModalContentGetter, setIsModalOn } = modal;

    setOuterFrameClass("md:w-[416px] md:h-[580px]");
    updateModalContentGetter(() => {
      return (
        <UserInfoModal
          offerConstruct={props.offerConstruct}
          offerConstructTenure={props.offerConstructTenure}
          clientLogoUrl={props.clientLogoUrl}
          redemptionAmount={props.redemptionAmount}
          dueAmount={props.dueAmount}
          dueAmount2={props.dueAmount2}
          brandDetails={props.brandDetails}
          selectedAmount={props.selectedAmount}
          email={props.email}
          phone={props.phone}
          name={props.name}
          onProfileUpdateSuccess={props.onProfileUpdateSuccess}
          subtitle={props.subtitle}
        />
      );
    });
    setIsModalOn(true);
  };
}

export default function BrandPage() {
  onMount(() => {
    // needed for the working of login modals
    attachPopStateListener();
  });

  const params = useParams();
  const isRouting = useIsRouting();
  const openUserInfoModal = createUserInfoModalHandler();

  const routeData: Accessor<BrandL2RouteData | undefined> =
    createAsync<BrandL2RouteData>(
      () => {
        return getBrandL2RouteData$C(params.brandKey);
      },
      {
        deferStream: true,
      }
    );

  const modalContext = useModal();

  const brandPageData = useBrandPage({
    brandKey: params.brandKey,
    openWarningModal: (props) => openWarningModal({ ...props, modalContext }),
    routeData: routeData,
    openPaymentLoader: openPaymentLoader,
    openUserInfoModal: openUserInfoModal || (() => { }),
  });

  const { getUIConfig } = brandPageData;

  return (
    <>
      <ClientOnlyComponent component={ClientComponent.ModalHost} />
      <ClientOnlyComponent component={ClientComponent.SnackbarHost} />
      <div>
        <Show when={getUIConfig().showCloseButtonOnTopRight}>
          <SdkTopRightCloseButton />
        </Show>
        <Show
          when={routeData() && !isRouting()}
          fallback={<DottedLoader color="#999" />}
        >
          <BrandPageComponent brandPageData={brandPageData} />
        </Show>
      </div>
    </>
  );
}